import React, {FC, useCallback, useRef} from "react";
import {List} from "@material-ui/core";
import {useCurrentShow, useCurrentShowId} from "hooks";
import {useNavigate} from "react-router-dom";
import NotificationItem from "view/components/AppBar/components/NotificationsPopup/components/NotificationItem";
import {getReadableString} from "constraints/general";
import {
  INotificationBar,
  INotificationMetaDataTaskComment,
} from "@evennt-tech/api-v2-core";

interface NotificationListProps {
  notifications: INotificationBar[];
  isNewNotifications: boolean;
  markOneNotificationAsRead: (notificationId: string) => Promise<void>;
  setInViewNotificationIds: React.Dispatch<React.SetStateAction<string[]>>;
  isLoading: boolean;
  dateFormat?: string;
}

const NotificationList: FC<NotificationListProps> = ({
  notifications,
  markOneNotificationAsRead,
  setInViewNotificationIds,
  dateFormat: dateFormatOverride,
}) => {
  const showId = useCurrentShowId();
  const [currentShow] = useCurrentShow({showId});
  const dateFormat =
    dateFormatOverride || getReadableString(currentShow).toUpperCase();
  const navigate = useNavigate();

  const observerRef = useRef<IntersectionObserver | null>(null);
  const observeNotification = useCallback<React.RefCallback<HTMLLIElement>>(
    (node) => {
      if (!node) {
        return;
      }

      if (observerRef.current) {
        observerRef.current.observe(node);
        return;
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            const notificationId = entry.target.getAttribute("data-id");
            const notificationStatus = entry.target.getAttribute("data-status");

            if (!notificationId || !notificationStatus) {
              return;
            }

            setInViewNotificationIds((prev) => {
              const updatedNotificationIds = [...prev];

              const shouldUpdateUnreadNotification =
                notificationStatus === "unread" &&
                !updatedNotificationIds.includes(notificationId);
              const shouldUpdateOpenedNotification =
                notificationStatus === "opened" &&
                !updatedNotificationIds.includes(notificationId);

              if (shouldUpdateUnreadNotification) {
                updatedNotificationIds.push(notificationId);
              }

              if (shouldUpdateOpenedNotification) {
                updatedNotificationIds.push(notificationId);
              }

              return updatedNotificationIds;
            });
          });
        },
        {
          root: null,
          threshold: 0.8,
        }
      );
    },
    []
  );

  const handleOpenTask = (
    notification: INotificationBar<string, INotificationMetaDataTaskComment>
  ) => async () => {
    await markOneNotificationAsRead(notification.id);
    if (notification && notification.metadata?.url) {
      window.open(notification.metadata.url, "_self");
    }
  };

  return (
    <List>
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          observeNotification={observeNotification}
          onTaskOpen={handleOpenTask(notification)}
          dateFormat={dateFormat}
        />
      ))}
    </List>
  );
};

export default NotificationList;
